import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Channel,
  ChannelGuide,
  ChannelGuideSchema,
  TransportStream,
} from '@/lib/schema/channelGuide';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { XMLParser } from 'fast-xml-parser';
import { v4 as uuidv4 } from 'uuid';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type ChannelGuideFormProps = {
  channelGuideId?: string;
  channelGuide?: ChannelGuide;
  onSubmit: (data: any) => void;
  mutation: any;
};

const channelGuideCisco = [
  { pin: '65555', value: '187', label: 'Channel Guide - F1' },
  {
    pin: '69661',
    value: '188',
    label: 'Channel Guide - Luxury Suites',
  },
  { pin: '80244', value: '189', label: 'Channel Guide - Miami Open' },
  { pin: '13482', value: '186', label: 'Channel Guide - Press Box' },
];

export function ChannelGuideForm({
  channelGuideId,
  channelGuide,
  onSubmit,
  mutation,
}: ChannelGuideFormProps) {
  const navigate = useNavigate();

  const form = useForm<ChannelGuide>({
    resolver: zodResolver(ChannelGuideSchema),
    defaultValues: {
      name: channelGuide?.name || '',
      channels: channelGuide?.channels || [],
      suiteId: channelGuide?.suiteId || '',
      pin: channelGuide?.pin || '',
    },
  });

  const handleImportTypeChange = () => {
    form.setValue('suiteId', '');
    form.setValue('channels', []);
    form.setValue('pin', '');
  };

  console.log('formState', form.watch());

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Channel guide name</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  inError={!!form.formState.errors.name}
                  placeholder="Enter channel guide name"
                  {...field}
                />
              </FormControl>
              <FormMessage>{fieldState.error?.message || ' '}</FormMessage>
            </FormItem>
          )}
        />
        {!channelGuideId && (
          <Tabs defaultValue="channelId" onValueChange={handleImportTypeChange}>
            <TabsList>
              <TabsTrigger value="channelId">
                Import from VisionEDGE
              </TabsTrigger>
              <TabsTrigger value="file">Import from file</TabsTrigger>
            </TabsList>
            <TabsContent value="channelId">
              <FormField
                control={form.control}
                name="suiteId"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Channel guide source</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        form.setValue(
                          'pin',
                          channelGuideCisco.find((cmd) => cmd.value === value)
                            ?.pin || ''
                        );
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {channelGuideCisco.map((cmd) => (
                          <SelectItem key={cmd.value} value={cmd.value}>
                            {cmd.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage>
                      {fieldState.error?.message || ' '}
                    </FormMessage>
                  </FormItem>
                )}
              />
            </TabsContent>
            <TabsContent value="file">
              <FormField
                control={form.control}
                name="channels"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Import channels</FormLabel>
                    <FormControl>
                      <Input
                        type="file"
                        autoComplete="off"
                        inError={!!form.formState.errors.channels}
                        placeholder="Enter controller name"
                        name={field.name}
                        onChange={async (e) => {
                          const content = await e.target.files?.[0]?.text();
                          if (!content) {
                            return;
                          }

                          const parser = new XMLParser();
                          let xmlChannels = parser.parse(content);

                          const channels = xmlChannels?.channels?.channel?.map(
                            (xmChannel: any): Channel => {
                              // Parse transportStream URL to extract protocol, IP and port
                              if (
                                xmChannel.physicalChannel &&
                                typeof xmChannel.physicalChannel === 'string'
                              ) {
                                const transportStreamUrl =
                                  xmChannel.physicalChannel;
                                const match = transportStreamUrl.match(
                                  /^([\w]+):\/\/([\d.]+):(\d+)/
                                );

                                if (match) {
                                  const [_, protocol, ip, port] = match;
                                  const transportStream = (
                                    p: string
                                  ): TransportStream => {
                                    switch (p) {
                                      case 'tsoverudp':
                                        return TransportStream.UDP;
                                      case 'tsoverrtp':
                                        return TransportStream.RPT;
                                      case 'http':
                                        return TransportStream.HTTP;
                                      default:
                                        return TransportStream.UDP; // Default to UDP if unknown
                                    }
                                  };

                                  xmChannel.protocol =
                                    transportStream(protocol);
                                  xmChannel.ip = ip;
                                  xmChannel.port = parseInt(port, 10);
                                }
                              }

                              return {
                                id: uuidv4(),
                                name: xmChannel.name,
                                ip: xmChannel.ip,
                                pnum: xmChannel.logicalChannel,
                                port: xmChannel.port,
                                activated: true,
                                transportStream: xmChannel.protocol,
                              };
                            }
                          );
                          field.onChange(channels);
                        }}
                      />
                    </FormControl>
                    {fieldState.error?.message && (
                      <FormMessage>{fieldState.error?.message}</FormMessage>
                    )}
                    <FormDescription>
                      You are about to import{' '}
                      <strong>{field.value.length}</strong> channels
                    </FormDescription>
                  </FormItem>
                )}
              />
            </TabsContent>
          </Tabs>
        )}
        <div className="flex gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/channel-guides')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {channelGuideId
              ? mutation.isPending
                ? 'Updating...'
                : 'Update channel guide'
              : mutation.isPending
                ? 'Creating...'
                : 'Create channel guide'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
